import React, { Children, FC, isValidElement, ReactNode, useState } from 'react';
import { Button, ButtonProps, Popover, PopoverProps } from '@mui/material';
import { nanoid } from 'nanoid';

import { ClearIcon } from 'src/shared/components/icons';
import { useTranslate } from 'src/shared/providers';

import { DropdownTypes } from './Dropdown.types';
import { DropdownProvider } from './DropdownProvider';

type DropdownExtendsType = Pick<ButtonProps, 'size' | 'variant' | 'color' | 'sx'> &
  Pick<PopoverProps, 'anchorOrigin' | 'transformOrigin'>;

interface Props extends DropdownExtendsType {
  title: ReactNode;
  onSelect: (value: any, newActive: boolean) => void;
  children: ReactNode;
  defaultValues?: any[];
  icon?: ReactNode;
  activeIcon?: ReactNode;
  className?: string;
  popperClassName?: string;
  disabled?: boolean;
  showAriaText?: boolean;
  showActive?: boolean;
  type?: DropdownTypes;
  id?: string;
  onClose?: () => void;
  onClear?: () => void;
}

export const Dropdown: FC<Props> = ({
  icon,
  activeIcon,
  anchorOrigin,
  transformOrigin,
  size = 'small',
  variant,
  color,
  title,
  defaultValues = [],
  disabled,
  showAriaText = false,
  showActive = false,
  type,
  onSelect,
  onClose,
  onClear,
  popperClassName = '',
  className,
  id: propId = nanoid(),
  children,
  sx,
}) => {
  const { t } = useTranslate();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedValues, setSelectedValues] = useState<any[]>(defaultValues);
  const [selectedAriaText, setSelectedAriaText] = useState<string>('');
  const hasSelectedValues = !!selectedValues.length;

  const fullTitle = getTitle(title, selectedValues.length, selectedAriaText, showActive, showAriaText);

  const open = Boolean(anchorEl);
  const id = open ? propId : undefined;

  const showActiveButton =
    showActive && (hasSelectedValues || open) && (!showAriaText || (showAriaText && selectedAriaText));

  const handleSelect = (value: string, newActive: boolean, ariaText: string | undefined) => {
    if (selectedValues.includes(value)) {
      setSelectedValues(selectedValues.filter((v) => v !== value));
    } else {
      if (type !== 'checkbox') {
        setSelectedValues([value]);
      } else {
        setSelectedValues([...selectedValues, value]);
      }
    }

    if (!onSelect) {
      return;
    }

    setSelectedAriaText(ariaText || '');
    onSelect(value, newActive);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onClose) {
      onClose();
    }
  };

  const handleClear = () => {
    setSelectedValues([]);

    if (!onClear) {
      return;
    }

    onClear();
  };

  return (
    <>
      <Button
        aria-describedby={id}
        className={className}
        size={size}
        variant={variant || showActiveButton ? 'contained' : 'text'}
        color={color || showActiveButton ? 'primary' : 'inherit'}
        disabled={disabled}
        startIcon={showActiveButton && hasSelectedValues && activeIcon ? activeIcon : icon}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {fullTitle}
      </Button>
      <Popover
        classes={{ paper: `${popperClassName} p-4 mt-1 shadow` }}
        sx={sx}
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        transformOrigin={transformOrigin}
        anchorOrigin={anchorOrigin || { vertical: 'bottom', horizontal: 'left' }}
      >
        <>
          {Children.map(children, (child) => {
            if (!isValidElement(child) || !child.props) {
              return child;
            }
            const { value: childValue, ariaText: childAriaText } = child.props;

            const isActive = selectedValues.includes(childValue);

            return (
              <DropdownProvider
                active={isActive}
                type={type}
                onSelect={(newValue) => handleSelect(newValue, !isActive, childAriaText)}
              >
                <div className="cursor-pointer">{child}</div>
              </DropdownProvider>
            );
          })}
          {onClear && (
            <Button
              className="mt-2"
              size="small"
              color="secondary"
              onClick={handleClear}
              startIcon={<ClearIcon />}
              disabled={!hasSelectedValues}
            >
              {t('global.clear')}
            </Button>
          )}
        </>
      </Popover>
    </>
  );
};

const getTitle = (
  title: ReactNode,
  count: number,
  ariaText: string,
  showActive: boolean,
  showAriaText: boolean,
): ReactNode => {
  if (!showActive) {
    return title;
  }

  if (count <= 0) {
    return title;
  }

  if (showAriaText) {
    if (!ariaText) {
      return title;
    }

    return `${title} ${ariaText}`;
  }

  return `${title} (+${count})`;
};
